import { useEffect, useLayoutEffect, useRef, useState, VoidFunctionComponent } from 'react';
import { useUnmount } from 'react-use';
import {
    PixelationEffect,
    EffectComposer,
    BlendFunction,
    DepthDownsamplingPass,
    EdgeDetectionMode,
    EffectPass,
    NormalPass,
    PredicationMode,
    OverrideMaterialManager,
    RenderPass,
    Resolution,
    SelectiveBloomEffect,
    SMAAEffect,
    SMAAPreset,
    SSAOEffect,
    TextureEffect,
    NoiseEffect,
    ChromaticAberrationEffect,
    HueSaturationEffect,
    BloomEffect,
} from 'postprocessing';
import { HalfFloatType, RepeatWrapping, Texture, UVMapping, Vector2 } from 'three';
import { useTexture } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { MOSAIC_TRANSITION_DURATION } from '@/lib/globals';
import mosaicOutSound from '@/sounds/ob-sfx-mosaic-out.mp3';
import mosaicInSound from '@/sounds/ob-sfx-mosaic-in.mp3';
import { clampedLerp } from '@/lib/mathUtils';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import crtImage from '@/img/crt-aperture.png';
import { useBloomSelectionContext } from '@/context/BloomSelectionContext';
import { useEffectContext } from '@/hooks/useEffectContext';
import { OnlybotsScene } from '@/lib/types';
import { deviceProperties } from '@/lib/deviceProperties';

const EYE_BLOOM_INTENSITY = 4.0;
const PIXELATION_GRANULARITY = 2048 * 1.3; // we want to hit 2048, so give an extra margin of 30%
const PIXELATION_EASEOUT_FACTOR = 4; // the bigger the number, the more time we spend at the early part of the animation
const BLOOM_EASEOUT_FACTOR = 0.25;
const BLOOM_EASE_DURATION = 0.5;

const nearestPowerOf2 = (n: number): number => 1 << (31 - Math.clz32(n));

const calculateCrtRepeat = (texture: Texture, pixelRatio: number, width: number, height: number): void => {
    const textureWidth = 9;
    const textureHeight = 16;
    texture.repeat.set(width / pixelRatio / textureWidth, height / pixelRatio / textureHeight);
};

const max1PixelRatio = window.devicePixelRatio > 1 ? 1 : window.devicePixelRatio;
OverrideMaterialManager.workaroundEnabled = true;

interface Props {
    scene: OnlybotsScene;
}

const { isMobile } = deviceProperties();

export const PostProcessingEffect: VoidFunctionComponent<Props> = ({ scene: onlybotsScene }) => {
    const { pixelate, maxPixelGranularity, easeInBloom, setEaseInBloom } = useEffectContext();
    const pixelationTimer = useRef<number>(0);
    const bloomEaseTimer = useRef<number>(0);
    const increasePixelation = useRef<boolean>(false);
    const decreasePixelation = useRef<boolean>(false);
    const increaseBloom = useRef<boolean>(false);
    const [playMosaicOutSound] = useOnlybotsSound(mosaicOutSound);
    const [playMosaicInSound] = useOnlybotsSound(mosaicInSound);
    const pixelationEffect = useRef<PixelationEffect>(new PixelationEffect(0));
    const normalPass = useRef<NormalPass>();
    const depthDownsamplingPass = useRef<DepthDownsamplingPass>();
    const normalDepthBuffer = useRef<Texture | null>(null);
    const ssaoEffect = useRef<SSAOEffect>();
    const smaaEffect = useRef<SMAAEffect>();
    const botSceneBloomEffect = useRef<BloomEffect>();
    const defaultSceneBloomEffect = useRef<BloomEffect>();
    const eyeBloomEffect = useRef<SelectiveBloomEffect>();
    const crtTextureEffect = useRef<TextureEffect>();
    const noiseEffect = useRef<NoiseEffect>();
    const aberrationEffect = useRef<ChromaticAberrationEffect>();
    const saturationEffect = useRef<HueSaturationEffect>(
        new HueSaturationEffect({
            saturation: 0.14,
        })
    );
    const renderPass = useRef<RenderPass>();
    const defaultSceneEffectsPass = useRef<EffectPass>();
    const botSceneEffectsPass = useRef<EffectPass>();
    const editionSceneEffectsPass = useRef<EffectPass>();
    const crtPass = useRef<EffectPass>();
    const pixelationPass = useRef<EffectPass>();
    const effectComposer = useRef<EffectComposer>();
    const [initComplete, setInitComplete] = useState<boolean>(false);

    const { gl, scene, camera } = useThree();
    const crtTexture = useTexture(crtImage, (texture) => {
        if (!Array.isArray(texture)) {
            texture.mapping = UVMapping;
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
            calculateCrtRepeat(texture, gl.getPixelRatio(), gl.domElement.width, gl.domElement.height);
        }
    });

    useLayoutEffect(() => {
        const element = gl.domElement;

        // on scene changes, recalculate CRT repeat
        calculateCrtRepeat(
            crtTexture,
            onlybotsScene === 'EDITION' ? window.devicePixelRatio : max1PixelRatio,
            element.width,
            element.height
        );

        // on resize, recalculate CRT repeat
        const resizeListener = (): void => {
            if (crtTexture) {
                calculateCrtRepeat(crtTexture, gl.getPixelRatio(), element.width, element.height);
            }
        };
        element.addEventListener('resize', resizeListener);
        return (): void => {
            element.removeEventListener('resize', resizeListener);
        };
    }, [gl, crtTexture, onlybotsScene]);

    useEffect(() => {
        // Normal Pass
        // Only needed when using SSAO

        if (!normalPass.current) {
            normalPass.current = new NormalPass(scene, camera, {
                resolutionScale: 0.5,
            });
        } else {
            normalPass.current.mainCamera = camera;
            normalPass.current.mainScene = scene;
        }

        // Depth Downsampling Pass
        // Only needed when using SSAO

        if (!depthDownsamplingPass.current && normalPass.current) {
            depthDownsamplingPass.current = new DepthDownsamplingPass({
                normalBuffer: normalPass.current.texture,
                resolutionScale: 0.5,
            });
        }

        // GLES2 Optimization of normal Buffer
        // Only needed when using DDP above / SSAO

        if (!normalDepthBuffer.current && gl.capabilities.isWebGL2 && depthDownsamplingPass.current) {
            normalDepthBuffer.current = depthDownsamplingPass.current.texture;
        }

        // SSAO

        if (!ssaoEffect.current) {
            ssaoEffect.current = new SSAOEffect(camera, normalPass.current.texture, {
                blendFunction: BlendFunction.MULTIPLY,
                distanceScaling: true,
                depthAwareUpsampling: true,
                normalDepthBuffer: normalDepthBuffer.current ?? undefined,
                samples: 16,
                rings: 6,
                distanceThreshold: 1.32, // render up to a distance of 20wu
                distanceFalloff: 0.0025, // with ~2.5wu falloff
                rangeThreshold: 1.0003, // occlusion proximity of 0.3wu
                rangeFalloff: 0.0001, // with 0.1wu falloff
                luminanceInfluence: 0.86,
                minRadiusScale: 0.33,
                radius: 0.1,
                intensity: 24.0,
                bias: 1.025,
                fade: 0.04,
                color: undefined,
                resolutionScale: 0.5,
                worldProximityThreshold: undefined as unknown as number,
                worldProximityFalloff: undefined as unknown as number,
                worldDistanceFalloff: undefined as unknown as number,
                worldDistanceThreshold: undefined as unknown as number,
            });
        } else {
            ssaoEffect.current.mainCamera = camera;
        }

        // SMAA

        if (!smaaEffect.current) {
            smaaEffect.current = new SMAAEffect({
                preset: SMAAPreset.MEDIUM,
                edgeDetectionMode: EdgeDetectionMode.COLOR,
                predicationMode: PredicationMode.DEPTH,
            });

            smaaEffect.current.edgeDetectionMaterial.edgeDetectionThreshold = 0.02;
            smaaEffect.current.edgeDetectionMaterial.predicationThreshold = 0.002;
            smaaEffect.current.edgeDetectionMaterial.predicationScale = 1.0;
        }

        // Scene Bloom

        if (!botSceneBloomEffect.current) {
            botSceneBloomEffect.current = new BloomEffect({
                mipmapBlur: true,
                radius: 0.2,
                luminanceThreshold: 0.5,
                luminanceSmoothing: 0.025,
                intensity: 0.5,
                levels: 8,
            } as any);
        }

        if (!defaultSceneBloomEffect.current) {
            defaultSceneBloomEffect.current = new BloomEffect({
                mipmapBlur: true,
                radius: 0.2,
                luminanceThreshold: 0.5,
                luminanceSmoothing: 0.025,
                intensity: 0.5,
                levels: 8,
            } as any);
        }

        // Selective Bloom

        if (!eyeBloomEffect.current) {
            eyeBloomEffect.current = new SelectiveBloomEffect(scene, camera, {
                width: Resolution.AUTO_SIZE,
                height: Resolution.AUTO_SIZE,
                blendFunction: BlendFunction.ADD,
                mipmapBlur: true,
                luminanceThreshold: 0.0,
                luminanceSmoothing: 0.2,
                radius: 0.8,
                intensity: EYE_BLOOM_INTENSITY,
                levels: 4,
            } as any);

            eyeBloomEffect.current.selection.set([]);
            eyeBloomEffect.current.selection.layer = 10;
            eyeBloomEffect.current.ignoreBackground = true;
            eyeBloomEffect.current.inverted = false;
        } else {
            eyeBloomEffect.current.mainScene = scene;
            eyeBloomEffect.current.mainCamera = camera;
        }

        // CRT texture effect

        if (!crtTextureEffect.current) {
            crtTextureEffect.current = new TextureEffect({
                blendFunction: BlendFunction.OVERLAY,
                texture: crtTexture,
            });
        }
        // Noise effect

        if (!noiseEffect.current) {
            noiseEffect.current = new NoiseEffect({ premultiply: false });
            noiseEffect.current.blendMode.opacity.value = 0.2;
            noiseEffect.current.blendMode.blendFunction = BlendFunction.OVERLAY;
        }

        // Chroma aberration effect

        if (!aberrationEffect.current) {
            aberrationEffect.current = new ChromaticAberrationEffect({
                offset: new Vector2(128e-4, 5e-4),
                radialModulation: true,
                modulationOffset: 64.2,
            });
            aberrationEffect.current.blendMode.opacity.value = 0.8;
        }

        // RENDER PASSES

        // Main render pass

        if (!renderPass.current) {
            renderPass.current = new RenderPass(scene, camera);
        } else {
            renderPass.current.mainScene = scene;
            renderPass.current.mainCamera = camera;
        }

        // Title / Mint scenes render pass

        if (!botSceneEffectsPass.current) {
            botSceneEffectsPass.current = new EffectPass(
                camera,
                ssaoEffect.current,
                smaaEffect.current,
                eyeBloomEffect.current,
                botSceneBloomEffect.current
            );
        } else {
            botSceneEffectsPass.current.mainScene = scene;
            botSceneEffectsPass.current.mainCamera = camera;
        }

        // Edition scene render pass

        if (!editionSceneEffectsPass.current) {
            if (isMobile) {
                editionSceneEffectsPass.current = new EffectPass(camera, eyeBloomEffect.current);
            } else {
                editionSceneEffectsPass.current = new EffectPass(camera, smaaEffect.current, eyeBloomEffect.current);
            }
        } else {
            editionSceneEffectsPass.current.mainScene = scene;
            editionSceneEffectsPass.current.mainCamera = camera;
        }

        // All scenes (default)

        if (!defaultSceneEffectsPass.current) {
            defaultSceneEffectsPass.current = new EffectPass(camera, defaultSceneBloomEffect.current);
        } else {
            defaultSceneEffectsPass.current.mainScene = scene;
            defaultSceneEffectsPass.current.mainCamera = camera;
        }

        // CRT pass (on top of everything)

        if (!crtPass.current) {
            crtPass.current = new EffectPass(
                camera,
                // TODO: see if aberration effect is working
                // aberrationEffect.current,
                crtTextureEffect.current,
                noiseEffect.current,
                saturationEffect.current
            );
        } else {
            crtPass.current.mainCamera = camera;
        }

        // Pixelation pass (used for transitions)
        if (!pixelationPass.current) {
            pixelationPass.current = new EffectPass(camera, pixelationEffect.current);
        } else {
            pixelationPass.current.mainCamera = camera;
        }

        // Init Effects Composer

        if (!effectComposer.current) {
            effectComposer.current = new EffectComposer(gl, {
                depthBuffer: true,
                stencilBuffer: true,
                frameBufferType: HalfFloatType,
            });

            // this seems to be a touch smoother especially when animating...

            effectComposer.current.autoRenderToScreen = true;
        }
        setInitComplete(true);
    }, [gl, camera, scene, crtTexture, onlybotsScene]);

    useLayoutEffect(() => {
        if (initComplete && effectComposer.current) {
            effectComposer.current.removeAllPasses();
            if (renderPass.current) {
                effectComposer.current.addPass(renderPass.current);
            }

            if (onlybotsScene === 'TITLE' || onlybotsScene === 'REVEAL' || onlybotsScene === 'EDITION') {
                if (normalPass.current) {
                    effectComposer.current.addPass(normalPass.current);
                }

                if (gl.capabilities.isWebGL2 && depthDownsamplingPass.current) {
                    effectComposer.current.addPass(depthDownsamplingPass.current);
                }
            }

            if ((onlybotsScene === 'TITLE' || onlybotsScene === 'REVEAL') && botSceneEffectsPass.current) {
                effectComposer.current.addPass(botSceneEffectsPass.current);
            } else if (onlybotsScene === 'EDITION' && editionSceneEffectsPass.current) {
                effectComposer.current.addPass(editionSceneEffectsPass.current);
            } else if (defaultSceneEffectsPass.current) {
                effectComposer.current.addPass(defaultSceneEffectsPass.current);
            }

            if (pixelationPass.current) {
                effectComposer.current.addPass(pixelationPass.current);
            }

            if (crtPass.current && crtTextureEffect.current && noiseEffect.current) {
                if (onlybotsScene === 'TITLE' || onlybotsScene === 'REVEAL') {
                    crtTextureEffect.current.blendMode.opacity.value = 0.4;
                    noiseEffect.current.blendMode.opacity.value = 0.2;
                } else if (onlybotsScene === 'EDITION') {
                    crtTextureEffect.current.blendMode.opacity.value = 0.05;
                    noiseEffect.current.blendMode.opacity.value = 0.05;
                } else {
                    crtTextureEffect.current.blendMode.opacity.value = 0.8;
                    noiseEffect.current.blendMode.opacity.value = 0.2;
                }

                effectComposer.current.addPass(crtPass.current);
            }
        }
    }, [crtTexture, gl.capabilities.isWebGL2, initComplete, onlybotsScene]);

    const { selections } = useBloomSelectionContext();

    useUnmount(() => {
        crtTexture.dispose();
        pixelationEffect.current.dispose();
        normalPass?.current?.dispose();
        depthDownsamplingPass?.current?.dispose();
        ssaoEffect?.current?.dispose();
        smaaEffect?.current?.dispose();
        defaultSceneBloomEffect?.current?.dispose();
        botSceneBloomEffect?.current?.dispose();
        eyeBloomEffect?.current?.dispose();
        crtTextureEffect?.current?.dispose();
        noiseEffect?.current?.dispose();
        aberrationEffect?.current?.dispose();
        saturationEffect?.current?.dispose();
        renderPass?.current?.dispose();
        botSceneEffectsPass?.current?.dispose();
        defaultSceneEffectsPass?.current?.dispose();
        editionSceneEffectsPass?.current?.dispose();
        crtPass?.current?.dispose();
        pixelationPass?.current?.dispose();
        effectComposer?.current?.dispose();
    });

    useLayoutEffect(() => {
        eyeBloomEffect?.current?.selection?.set([...selections]);
    }, [selections]);

    useLayoutEffect(() => {
        if (pixelate) {
            increasePixelation.current = true;
            pixelationTimer.current = 0;
        }
    }, [pixelate]);

    useLayoutEffect(() => {
        if (maxPixelGranularity) {
            pixelationEffect.current.granularity = nearestPowerOf2(PIXELATION_GRANULARITY);
            decreasePixelation.current = true;
            pixelationTimer.current = -0.75;
        }
    }, [maxPixelGranularity]);

    useLayoutEffect(() => {
        // if easing bloom in, set to zero and update ref, then ease in in useFrame
        if (easeInBloom) {
            increaseBloom.current = true;
            if (eyeBloomEffect.current) {
                eyeBloomEffect.current.intensity = 0;
            }
            setEaseInBloom(false);
        }
    }, [easeInBloom, setEaseInBloom]);

    useFrame((_, delta) => {
        if (pixelationTimer.current === 0) {
            if (increasePixelation.current) {
                playMosaicOutSound();
            } else if (decreasePixelation.current) {
                playMosaicInSound();
            }
        }

        if (increasePixelation.current || decreasePixelation.current) {
            pixelationTimer.current += delta;
        }

        // when we're increasing pixelation we decrease eye bloom
        if (increasePixelation.current) {
            if (pixelationTimer.current < MOSAIC_TRANSITION_DURATION) {
                pixelationEffect.current.granularity = clampedLerp(
                    0,
                    PIXELATION_GRANULARITY,
                    pixelationTimer.current / MOSAIC_TRANSITION_DURATION,
                    PIXELATION_EASEOUT_FACTOR
                );
                if (eyeBloomEffect.current) {
                    eyeBloomEffect.current.intensity = clampedLerp(
                        EYE_BLOOM_INTENSITY,
                        0,
                        pixelationTimer.current / MOSAIC_TRANSITION_DURATION,
                        BLOOM_EASEOUT_FACTOR
                    );
                }
            } else {
                decreasePixelation.current = true;
                increasePixelation.current = false;
                pixelationTimer.current = 0;
            }
        } else if (decreasePixelation.current) {
            if (pixelationTimer.current < MOSAIC_TRANSITION_DURATION) {
                pixelationEffect.current.granularity = clampedLerp(
                    0,
                    PIXELATION_GRANULARITY,
                    1 - pixelationTimer.current / MOSAIC_TRANSITION_DURATION,
                    PIXELATION_EASEOUT_FACTOR
                );
                // we don't want eyebloom on start of MINT, we scale it up during the explosion
                if (eyeBloomEffect.current && onlybotsScene !== 'REVEAL') {
                    eyeBloomEffect.current.intensity = clampedLerp(
                        EYE_BLOOM_INTENSITY,
                        0,
                        1 - pixelationTimer.current / MOSAIC_TRANSITION_DURATION,
                        BLOOM_EASEOUT_FACTOR
                    );
                }
            } else {
                decreasePixelation.current = false;
                pixelationEffect.current.granularity = 0;
                pixelationTimer.current = 0;
                if (eyeBloomEffect.current && onlybotsScene !== 'REVEAL') {
                    eyeBloomEffect.current.intensity = EYE_BLOOM_INTENSITY;
                }
            }
        }

        if (increaseBloom.current && eyeBloomEffect.current) {
            bloomEaseTimer.current += delta;

            if (bloomEaseTimer.current < BLOOM_EASE_DURATION) {
                eyeBloomEffect.current.intensity = clampedLerp(
                    0,
                    EYE_BLOOM_INTENSITY,
                    bloomEaseTimer.current / BLOOM_EASE_DURATION,
                    1
                );
            } else {
                increaseBloom.current = false;
                eyeBloomEffect.current.intensity = EYE_BLOOM_INTENSITY;
                bloomEaseTimer.current = 0;
            }
        }
        // at larger granularity values, restrict to powers of 2
        if (pixelationEffect.current.granularity > 512) {
            pixelationEffect.current.granularity = nearestPowerOf2(pixelationEffect.current.granularity);
        }
        effectComposer?.current?.render(delta);
    }, 1);

    return null;
};
